import _ from 'lodash';
import { MockServiceTemplateDetailActionTypes } from 'store/mockServiceTemplates/mockServiceTemplateDetail/mockServiceTemplateDetailActionTypes';
import { ShuttlePaneControls, TransactionShuttleState } from 'store/transactionShuttle/TransactionShuttleTypes';
import { MsDetailParametersTab } from '../../mockServices/mockServiceTypes';
import { AssignmentActionType } from '../../../components/transactionsShuttle/TransactionShuttle.types';
import { AssetAssignmentBundle } from '../../../views/MockServices/Tabs/MockServices/SslConfiguration';
import { MessagingConfigTabs, RunnerTabType } from '../../mockServices/mockServiceDetail/mockServiceDetailTypes';
import { ShuttleState } from '../../../components/shuttle/Shuttle';

export const initialShuttleState: any = {
  leftPane: {
    searchTerm: '',
    selected: [],
    activePage: 1,
    expanded: null,
    totalResults: 0,
    isLoading: true,
    pageSize: 8,
  },
  rightPane: {
    searchTerm: '',
    selected: [],
    activePage: 1,
    expanded: null,
    totalResults: 0,
    isLoading: true,
    pageSize: 10,
    showPriorityModal: false,
  },
  leftPaneTransactions: [],
  rightPaneTransactions: [],
  isMoveLeftInProgress: false,
  isMoveRightInProgress: false,
};

export interface MockServiceTemplateDetailState {
  activeTab: string;
  activeParamsTab: MsDetailParametersTab;
  shuttle: TransactionShuttleState;
  assets: {
    isLoading: boolean;
    assetBundle: AssetAssignmentBundle | null;
  };
  messagingConfig: {
    activeTab: MessagingConfigTabs;
  };
  runnerTab: RunnerTabType;
  httpRunnerTab: string;
  messagingRunnerTab: string;
  recordingConfigTab: 'settings' | 'recordedMessages';
  recordingShuttle: ShuttleState;
}

const DEFAULT_STATE: MockServiceTemplateDetailState = {
  activeTab: 'main',
  activeParamsTab: 'THINK_TIME',
  shuttle: initialShuttleState,
  assets: {
    isLoading: false,
    assetBundle: {
      assets: null,
    },
  },
  messagingConfig: {
    activeTab: 'basics',
  },
  runnerTab: 'httpRunner',
  httpRunnerTab: 'main',
  messagingRunnerTab: 'main',
  recordingConfigTab: 'settings',
  recordingShuttle: initialShuttleState,
};

export const mockServiceTemplateDetailReducer = (
  state: MockServiceTemplateDetailState = DEFAULT_STATE,
  action: MockServiceTemplateDetailActionTypes,
): MockServiceTemplateDetailState => {
  switch (action.type) {
    case 'TEMPLATE_UPDATE_SHUTTLE_TRANSACTION': {
      const stateCopy = _.cloneDeep(state);
      let transactionList = [];
      if (action.payload.type === 'LEFT') {
        transactionList = stateCopy.shuttle.leftPaneTransactions;
      } else {
        transactionList = stateCopy.shuttle.rightPaneTransactions;
      }
      const indexToUpdate = transactionList.findIndex(item => item.id === action.payload.transaction.id);
      if (indexToUpdate > -1) {
        transactionList[indexToUpdate] = action.payload.transaction;
      }
      return stateCopy;
    }

    case 'TEMPLATE_RECORDING_SHUTTLE_UPDATE_IN_PROGRESS': {
      const update = { ...state };
      const { isMoveLeftInProgress, isMoveRightInProgress } = action.payload;

      if (isMoveLeftInProgress !== undefined) {
        update.recordingShuttle.isMoveLeftInProgress = isMoveLeftInProgress;
      }

      if (isMoveRightInProgress !== undefined) {
        update.recordingShuttle.isMoveRightInProgress = isMoveRightInProgress;
      }

      return update;
    }

    case 'TEMPLATE_RECORDING_SHUTTLE_UPDATE_PANE': {
      const update = _.cloneDeep(state);
      if (action.payload.position === 'LEFT') {
        return { ...update, recordingShuttle: { ...update.recordingShuttle, leftPane: action.payload.state } };
      } else {
        return { ...update, recordingShuttle: { ...update.recordingShuttle, rightPane: action.payload.state } };
      }
    }

    case 'TEMPLATE_DETAIL_UPDATE_ACTIVE_TAB':
      return { ...state, activeTab: action.payload };

    case 'TEMPLATE_DETAIL_UPDATE_ACTIVE_PARAM_TAB':
      return { ...state, activeParamsTab: action.payload };

    case 'TEMPLATE_DETAIL_RESET_SHUTTLE':
      return { ...state, shuttle: initialShuttleState };

    case 'TEMPLATE_UPDATE_SHUTTLE_MOVE_IN_PROGRESS':
      const inProgressState = _.cloneDeep(state);
      if (action.payload.type === AssignmentActionType.ADD) {
        inProgressState.shuttle.isMoveLeftInProgress = action.payload.inProgress;
      } else {
        inProgressState.shuttle.isMoveRightInProgress = action.payload.inProgress;
      }

      //Reset select all states
      inProgressState.shuttle.leftPane.selectAll = undefined;
      inProgressState.shuttle.rightPane.selectAll = undefined;

      return inProgressState;

    case 'TEMPLATE_DETAIL_SET_ASSET_BUNDLE': {
      const stateCopy = _.cloneDeep(state);
      stateCopy.assets.assetBundle = action.payload;
      return stateCopy;
    }

    case 'TEMPLATE_UPDATE_SHUTTLE_ACTIVE_PAGE':
      const paginationState = _.cloneDeep(state);
      if (action.payload.type === 'LEFT') {
        paginationState.shuttle.leftPane.activePage = action.payload.activePage;
      } else {
        paginationState.shuttle.rightPane.activePage = action.payload.activePage;
      }
      return paginationState;

    case 'TEMPLATE_UPDATE_SHOW_PRIORITY_MODAL': {
      const updated = _.cloneDeep(state);
      updated.shuttle.rightPane.showPriorityModal = action.payload;
      return updated;
    }

    case 'TEMPLATE_UPDATE_SHUTTLE_SORT': {
      const sortState = _.cloneDeep(state);
      if (action.payload.type === 'LEFT') {
        sortState.shuttle.leftPane.sort = action.payload.sort;
      } else {
        sortState.shuttle.rightPane.sort = action.payload.sort;
      }
      return sortState;
    }

    case 'TEMPLATE_UPDATE_RUNNER_TAB': {
      return { ...state, runnerTab: action.payload };
    }

    case 'TEMPLATE_HTTP_RUNNER_UPDATE_TAB': {
      return { ...state, httpRunnerTab: action.payload };
    }

    case 'TEMPLATE_MESSAGING_RUNNER_UPDATE_TAB': {
      return { ...state, messagingRunnerTab: action.payload };
    }

    case 'TEMPLATE_MSG_CONFIG_SET_ACTIVE_TAB': {
      return { ...state, messagingConfig: { activeTab: action.payload } };
    }

    case 'TEMPLATE_UPDATE_SHUTTLE_SEARCH_TERM':
      const searchTermState = _.cloneDeep(state);
      if (action.payload.type === 'LEFT') {
        searchTermState.shuttle.leftPane.searchTerm = action.payload.searchTerm;
      } else {
        searchTermState.shuttle.rightPane.searchTerm = action.payload.searchTerm;
      }
      return searchTermState;

    case 'TEMPLATE_UPDATE_SHUTTLE_TRANSACTION_SELECTION': {
      const transactionSelectionState = _.cloneDeep(state);
      const selector = action.payload.type === 'LEFT' ? 'leftPane' : 'rightPane';
      const isSelectAll = action.payload.selectAll === 'ALL';
      transactionSelectionState.shuttle[selector].selected = isSelectAll ? [] : action.payload.transactions;
      transactionSelectionState.shuttle[selector].selectAll = action.payload.selectAll;
      return transactionSelectionState;
    }

    case 'TEMPLATE_UPDATE_SHUTTLE_TRANSACTIONS':
      const updatedTransactionsState = _.cloneDeep(state);
      let paneControls: ShuttlePaneControls;
      if (action.payload.type === 'LEFT') {
        paneControls = updatedTransactionsState.shuttle.leftPane;
        updatedTransactionsState.shuttle.leftPaneTransactions = action.payload.transactions;
      } else {
        paneControls = updatedTransactionsState.shuttle.rightPane;
        updatedTransactionsState.shuttle.rightPaneTransactions = action.payload.transactions;
      }

      // Update total results
      paneControls.totalResults = action.payload.totalResults;
      paneControls.isLoading = false;
      return updatedTransactionsState;

    case 'TEMPLATE_DETAIL_PANE_IS_LOADING':
      const isLoadingState = _.cloneDeep(state);
      if (action.payload.paneType === 'LEFT') {
        isLoadingState.shuttle.leftPane.isLoading = action.payload.isLoading;
      } else {
        isLoadingState.shuttle.rightPane.isLoading = action.payload.isLoading;
      }
      return isLoadingState;

    case 'TEMPLATE_SHUTTLE_EXPAND_ITEM':
      const expandItemState = _.cloneDeep(state);
      let shuttlePane;
      if (action.payload.paneType === 'LEFT') {
        shuttlePane = expandItemState.shuttle.leftPane;
      } else {
        shuttlePane = expandItemState.shuttle.rightPane;
      }

      //Toggle pane
      if (shuttlePane.expanded === null || shuttlePane.expanded !== action.payload.expandItemId) {
        shuttlePane.expanded = action.payload.expandItemId;
      } else {
        shuttlePane.expanded = null;
      }
      return expandItemState;

    case 'TEMPLATE_DETAIL_RESET_VIEW':
      return DEFAULT_STATE;

    default:
      return state;
  }
};
